/**
 * TaskGantt — lightweight SVG Gantt chart.
 *
 * Renders one row per task in the window, with:
 *   - timeline header (day grid + weekend shading)
 *   - bar coloured by status
 *   - dependency arrows (FS = predecessor end -> successor start)
 *   - optional zoom (day-pixels) for the host page to control density
 *
 * No external library — meets CLAUDE.md "Gantt は SVG 自前実装".
 */
import { useMemo } from "react";

import { formatDate, isOverdue } from "./format";
import { STATUS_LABELS_JA } from "./types";
import type { GanttView, Task, TaskStatus } from "./types";

const ONE_DAY = 24 * 60 * 60 * 1000;
const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 36;
const LABEL_WIDTH = 240;
const DAY_PIXELS_DEFAULT = 24;

const STATUS_FILL: Record<TaskStatus, string> = {
  todo: "#cbd5e1",
  doing: "#fbbf24",
  review: "#a78bfa",
  done: "#34d399",
};

const STATUS_STROKE: Record<TaskStatus, string> = {
  todo: "#64748b",
  doing: "#b45309",
  review: "#6d28d9",
  done: "#047857",
};

export interface TaskGanttProps {
  view: GanttView | null;
  /** Pixels per day (zoom). Default 24. */
  dayWidth?: number;
  onSelect?: (task: Task) => void;
}

export function TaskGantt({
  view,
  dayWidth = DAY_PIXELS_DEFAULT,
  onSelect,
}: TaskGanttProps) {
  const layout = useMemo(() => {
    if (!view || view.bars.length === 0) {
      return null;
    }
    const startDay = startOfDay(view.from);
    const endDay = startOfDay(view.to) + ONE_DAY;
    const days = Math.max(1, Math.ceil((endDay - startDay) / ONE_DAY));
    const width = LABEL_WIDTH + days * dayWidth;
    const height = HEADER_HEIGHT + view.bars.length * ROW_HEIGHT + 8;

    const xForTs = (ts: number): number =>
      LABEL_WIDTH + Math.max(0, (ts - startDay) / ONE_DAY) * dayWidth;
    const yForRow = (i: number): number => HEADER_HEIGHT + i * ROW_HEIGHT;

    const rowIndex = new Map<string, number>();
    view.bars.forEach((b, i) => rowIndex.set(b.task.id, i));

    return { startDay, endDay, days, width, height, xForTs, yForRow, rowIndex };
  }, [view, dayWidth]);

  if (!view || view.bars.length === 0 || !layout) {
    return (
      <div
        data-testid="gantt-empty"
        className="rounded-md border border-dashed border-border-default bg-canvas p-12 text-center"
      >
        <p className="text-sm font-medium text-fg-primary">
          この期間に表示できるタスクがありません。
        </p>
        <p className="mt-1 text-xs text-fg-muted">
          タスクの「開始日」または「期限」を設定するとここに表示されます。
        </p>
      </div>
    );
  }

  const { startDay, days, width, height, xForTs, yForRow, rowIndex } = layout;
  const todayX = xForTs(Date.now());

  return (
    <div
      className="overflow-hidden rounded-md bg-canvas shadow-sm ring-1 ring-border-subtle"
      data-testid="task-gantt"
    >
      <div className="flex items-center justify-between border-b border-border-subtle bg-surface-base px-3 py-2 text-xs">
        <Legend />
        <span className="text-fg-muted">
          {formatDate(startDay)} 〜 {formatDate(startDay + (days - 1) * ONE_DAY)}
        </span>
      </div>
      <div className="overflow-auto">
        <svg
          role="img"
          aria-label="タスクのガントチャート"
          width={width}
          height={height}
          className="block"
        >
          {/* Day grid + weekend background */}
          {Array.from({ length: days + 1 }, (_, i) => {
            const x = LABEL_WIDTH + i * dayWidth;
            const dayTs = startDay + i * ONE_DAY;
            const dow = new Date(dayTs).getDay();
            const isWeekend = dow === 0 || dow === 6;
            return (
              <g key={`grid-${i}`}>
                {isWeekend && i < days && (
                  <rect
                    x={x}
                    y={HEADER_HEIGHT}
                    width={dayWidth}
                    height={height - HEADER_HEIGHT}
                    fill={dow === 0 ? "#fef2f2" : "#eff6ff"}
                    opacity={0.45}
                  />
                )}
                <line
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={height}
                  stroke="#e5e7eb"
                  strokeWidth={1}
                />
              </g>
            );
          })}
          <line
            x1={0}
            x2={width}
            y1={HEADER_HEIGHT}
            y2={HEADER_HEIGHT}
            stroke="#d1d5db"
            strokeWidth={1}
          />

          {/* Date headers */}
          {Array.from({ length: days }, (_, i) => {
            const dayTs = startDay + i * ONE_DAY;
            const x = LABEL_WIDTH + i * dayWidth;
            if (dayWidth < 18 && i % 2 === 1) return null;
            return (
              <text
                key={`hdr-${i}`}
                x={x + dayWidth / 2}
                y={20}
                fontSize={11}
                fill="#4b5563"
                textAnchor="middle"
                aria-label={formatDate(dayTs)}
              >
                <title>{formatDate(dayTs)}</title>
                {new Date(dayTs).getDate()}
              </text>
            );
          })}

          {/* Today marker */}
          {todayX >= LABEL_WIDTH && todayX <= width && (
            <g data-testid="gantt-today">
              <line
                x1={todayX}
                x2={todayX}
                y1={HEADER_HEIGHT - 4}
                y2={height}
                stroke="#dc2626"
                strokeWidth={1.5}
                strokeDasharray="4 2"
              />
              <text
                x={todayX + 3}
                y={HEADER_HEIGHT - 6}
                fontSize={10}
                fill="#dc2626"
                fontWeight={600}
              >
                今日
              </text>
            </g>
          )}

          {/* Row backgrounds */}
          {view.bars.map((bar, i) => (
            <rect
              key={`bg-${bar.task.id}`}
              x={0}
              y={yForRow(i)}
              width={width}
              height={ROW_HEIGHT}
              fill={i % 2 === 0 ? "#fafbfc" : "#ffffff"}
            />
          ))}

          {/* Labels (clickable) */}
          {view.bars.map((bar, i) => (
            <g
              key={`lbl-${bar.task.id}`}
              data-testid="gantt-label-row"
              role="button"
              tabIndex={0}
              aria-label={`${bar.task.title} の詳細を開く`}
              onClick={() => onSelect?.(bar.task)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect?.(bar.task);
                }
              }}
              style={{ cursor: onSelect ? "pointer" : "default" }}
            >
              <rect
                x={0}
                y={yForRow(i)}
                width={LABEL_WIDTH}
                height={ROW_HEIGHT}
                fill="transparent"
              />
              <text
                x={8}
                y={yForRow(i) + 19}
                fontSize={12}
                fill="#1f2937"
                data-testid="gantt-label"
              >
                {bar.task.title.length > 28
                  ? bar.task.title.slice(0, 27) + "…"
                  : bar.task.title}
              </text>
            </g>
          ))}

          {/* Vertical separator between labels & timeline */}
          <line
            x1={LABEL_WIDTH}
            x2={LABEL_WIDTH}
            y1={0}
            y2={height}
            stroke="#d1d5db"
            strokeWidth={1.2}
          />

          {/* Dependency arrows */}
          {view.bars.flatMap((bar, i) =>
            bar.blocked_by
              .filter((predId) => rowIndex.has(predId))
              .map((predId) => {
                const predIndex = rowIndex.get(predId)!;
                const predBar = view.bars[predIndex]!;
                const startX = xForTs(predBar.end_at);
                const startY = yForRow(predIndex) + ROW_HEIGHT / 2;
                const endX = xForTs(bar.start_at);
                const endY = yForRow(i) + ROW_HEIGHT / 2;
                const midX = Math.max(startX + 6, endX - 6);
                const path = `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
                return (
                  <g key={`dep-${predId}-${bar.task.id}`} data-testid="gantt-dep">
                    <path
                      d={path}
                      fill="none"
                      stroke="#94a3b8"
                      strokeWidth={1.2}
                    />
                    <polygon
                      points={`${endX},${endY} ${endX - 4},${endY - 3} ${endX - 4},${endY + 3}`}
                      fill="#94a3b8"
                    />
                  </g>
                );
              }),
          )}

          {/* Bars */}
          {view.bars.map((bar, i) => {
            const x = xForTs(bar.start_at);
            const w = Math.max(8, xForTs(bar.end_at) - x);
            const y = yForRow(i) + 5;
            const overdue = isOverdue(bar.task);
            return (
              <g
                key={`bar-${bar.task.id}`}
                data-testid="gantt-bar"
                data-task-id={bar.task.id}
                tabIndex={0}
                role="button"
                aria-label={`${bar.task.title} ${STATUS_LABELS_JA[bar.task.status]} ${formatDate(bar.start_at)} - ${formatDate(bar.end_at)}`}
                onClick={() => onSelect?.(bar.task)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect?.(bar.task);
                  }
                }}
                style={{ cursor: onSelect ? "pointer" : "default" }}
              >
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={ROW_HEIGHT - 10}
                  rx={4}
                  ry={4}
                  fill={STATUS_FILL[bar.task.status]}
                  stroke={overdue ? "#dc2626" : STATUS_STROKE[bar.task.status]}
                  strokeWidth={overdue ? 1.5 : 1}
                />
                {w > 60 && (
                  <text
                    x={x + 6}
                    y={y + 13}
                    fontSize={10}
                    fill="#1f2937"
                    pointerEvents="none"
                  >
                    {bar.task.priority} · {STATUS_LABELS_JA[bar.task.status]}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function Legend(): JSX.Element {
  const items: Array<{ status: TaskStatus; label: string }> = [
    { status: "todo", label: "ToDo" },
    { status: "doing", label: "Doing" },
    { status: "review", label: "Review" },
    { status: "done", label: "Done" },
  ];
  return (
    <div className="flex items-center gap-3" aria-label="凡例">
      {items.map((it) => (
        <span key={it.status} className="inline-flex items-center gap-1 text-fg-muted">
          <svg width={12} height={10} aria-hidden>
            <rect
              x={0}
              y={0}
              width={12}
              height={10}
              rx={3}
              ry={3}
              fill={STATUS_FILL[it.status]}
              stroke={STATUS_STROKE[it.status]}
              strokeWidth={1}
            />
          </svg>
          {it.label}
        </span>
      ))}
      <span className="ml-2 inline-flex items-center gap-1 text-fg-muted">
        <span
          aria-hidden
          className="inline-block h-3 w-1.5"
          style={{ background: "linear-gradient(to bottom, #dc2626, transparent)" }}
        />
        今日
      </span>
    </div>
  );
}

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
